/* eslint-disable camelcase */
/* eslint-disable no-undef */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* eslint-disable no-underscore-dangle*/

// eslint-disable-next-line import/no-cycle

const core = window.adobe_dc_sdk.appLauncher.coreFunction();
const {
  providers, auth2, locale2, logging, analytics,
} = core;

export const logger = logging.getLogger('TargetAPI');

class TargetAPI {
  constructor(options) {
    this.alloyOn = window.adobe_dc_sdk?.launchOptions?.alloyOn;
    this._alloyInitialized = false;
    this.TIMEOUT = 3000;
    this.options = {};
    if (this.alloyOn) {
      const optionsCopy = { ...options };
      if (optionsCopy?.mboxes && !optionsCopy?.decisionScopes) {
        optionsCopy.decisionScopes = optionsCopy.mboxes;
        delete optionsCopy.mboxes;
      }
      this.options = {
        decisionScopes: [],
        defaultParameters: {
          language: locale2.getLocale(),
        },
        imsEnabled: true,
        ...optionsCopy,
      };
      this._offers = { propositions: [] };
    } else {
      this.options = {
        mboxes: [],
        defaultParameters: {
          'data-language': locale2.getLocale(),
        },
        imsEnabled: true,
        ...options,
      };
      this._offers = { execute: { mboxes: [] } };
    }
    this._targetPromise = window.__targetLoadedPromise || new window.adobe_dc_sdk.util.Deferred();
    const replaceGetOffer = () => {
      // Replace getOffer method with custom one to avoid exception from error callback
      const g = window.adobe.target.getOffer;
      window.adobe.target.getOffer = params => {
        const p = params;
        p.error = () => {};
        return g(p);
      };
    };
    const targetPromiseHandler = (privileged, targetTimer) => {
      if (privileged) {
        this._targetPromise.reject(new Error('Target disabled for PAC user'));
        clearInterval(targetTimer);
        return;
      }
      if (this._alloyInitialized && window._satellite) {
        clearInterval(targetTimer);
        this._targetPromise.resolve(window._satellite);
      } else if (window.adobe.target) {
        replaceGetOffer();
        clearInterval(targetTimer);
        this._targetPromise.resolve(window.adobe.target);
      }
    };
    const handlePrivilege = targetTimer => {
      if (!auth2.isSignedIn) {
        targetPromiseHandler(false, targetTimer);
      } else {
        providers.limits2().then(limits2 => {
          targetPromiseHandler(limits2.isPrivilegedAction('marketing-integration'), targetTimer);
        });
      }
    };

    const initTarget = (optns, targetTimer) => {
      if (optns?.disableTarget === true) {
        clearInterval(targetTimer);
        this._targetPromise.reject(new Error('Target not initialized due to app override'));
        return;
      }
      // If target is not enabled, reject promise instead of waiting
      if (window.adobe.optIn?.isApproved('target') === false) {
        this._targetPromise.reject(new Error('Target opted out by user'));
        clearInterval(targetTimer);
        return;
      }
      if (!auth2.isSignedIn && window.adobe.target) {
        replaceGetOffer();
        this._targetPromise.resolve(window.adobe.target);
        clearInterval(targetTimer);
        return;
      }
      handlePrivilege(targetTimer);
    };

    const initAlloyTarget = (optns, targetTimer) => {
      this._alloyInitialized = true;

      if (optns?.disableTarget === true) {
        clearInterval(targetTimer);
        this._targetPromise.reject(new Error('Target not initialized due to app override'));
        return;
      }
      // target and analytics use the same consent category C0002
      const isTargetOptedOut = analytics.getConsent && !analytics.getConsent();
      if (!isTargetOptedOut) {
        if (!auth2.isSignedIn && window._satellite) {
          clearInterval(targetTimer);
          this._targetPromise.resolve(window._satellite);
          return;
        }
        handlePrivilege(targetTimer);
      } else {
        clearInterval(targetTimer);
        this._targetPromise.reject(new Error('Target opted out by user'));
      }
    };

    if (window.adobe?.target) {
      initTarget(options);
    } else if (
      this.alloyOn && window._satellite && Object.keys(window._satellite).length !== 0) {
      initAlloyTarget(options);
    } else {
      const targetTimer = setInterval(() => {
        if (
          this.alloyOn && window._satellite && Object.keys(window._satellite).length !== 0) {
          initAlloyTarget(options, targetTimer);
        } else if (window.adobe) {
          initTarget(options, targetTimer);
        }
      }, 1000);
    }
  }

  fetchProposition = (decisionScopes, decisionScopeParams) => new Promise((resolve, reject) => {
    let timeoutId;
    let timedout = false;
    const fetchHandler = result => {
      if (!timedout) {
        clearTimeout(timeoutId);
        resolve(result);
        window._satellite.track('propositionDisplay', result.propositions);
      }
    };

    timeoutId = setTimeout(() => {
      timedout = true;
      logger.error(`Target fetchProposition timeout: ${decisionScopes?.[0]}`);
      reject(new Error(`Target fetchProposition timeout: ${decisionScopes?.[0]}`));
    }, this.TIMEOUT);

    window._satellite.track('propositionFetch', {
      data: {
        __adobe: {
          target: decisionScopeParams,
        },
      },
      decisionScopes,
      done: fetchHandler,
    });
  });

  /**
     * @returns promise that resolves when target provider has been instantiated
     */
  ready = async () => {
    const self = this;
    const imsPromise = this.options.imsEnabled ? window.adobe_dc_sdk.loaded.imsReady : Promise.resolve(self);
    await imsPromise;
    let subscriptionsPromise;
    let abpTrialPromise;

    if (auth2.isSignedIn) {
      subscriptionsPromise = providers.user().then(u => u.getUser());
      abpTrialPromise = providers.user().then(u => u.isTrialUser());
    } else {
      subscriptionsPromise = Promise.resolve({
        identity: undefined,
        subscriptions: {
          subscriptions: [],
        },
      });
      abpTrialPromise = Promise.resolve(false);
    }

    try {
      const [t, { identity, subscriptions }, isTrialUser] = await Promise.all([this._targetPromise.promise, subscriptionsPromise, abpTrialPromise]);
      const s = subscriptions.subscriptions;

      const countryCode = identity ? identity.country_code : '';
      const language = identity ? identity.language : locale2.getLocale();
      const firstTimeUser = identity && identity.first_time && identity.first_time.dex_web_app;
      const subscriptionLevel = s.length > 0 ? s[0].level : '';
      const subscriptionName = s.length > 0 ? s[0].name : '';
      const userRole = s.length > 0 && s[0].level !== 'Free' ? s[0].name : '';

      if (self._alloyInitialized) {
        self._alloy = t;
        self.options.defaultParameters = {
          countryCode,
          language,
          firstTimeUser,
          subscriptionLevel,
          subscriptionName,
          userRole,
          isTrialUser,
        };
        const decisionScopes = self.options.decisionScopes.map(m => m.name);
        const decisionScopeParams = Object.assign(
          self.options.defaultParameters,
          self.options.decisionScopes.some(m => m.parameters) ? self.options.decisionScopes.map(m => m.parameters) : {},
        );
        if (decisionScopes.length > 0) {
          self._offers = await this.fetchProposition(decisionScopes, decisionScopeParams);
        } else {
          self._offers = { propositions: [] };
        }
        return self;
      }
      self._target = t;
      self.options.defaultParameters = {
        'data-countryCode': countryCode,
        'data-language': language,
        'data-firstTimeUser': firstTimeUser,
        'data-subscriptionlevel': subscriptionLevel,
        'data-subscriptionName': subscriptionName,
        'data-userRole': userRole,
        'data-isTrialUser': isTrialUser,
      };

      const mboxes = self.options.mboxes;
      mboxes.map((m, i) => {
        const mbox = m;
        mbox.parameters = Object.assign(self.options.defaultParameters, m.parameters || {});
        mbox.index = i;
        return mbox;
      });
      const consumerId = self.options.mboxes.map(m => m.name).join(',');
      if (mboxes.length > 0) {
        const response = await self._target.getOffers({
          consumerId,
          request: {
            execute: {
              mboxes,
            },
          },
        });
        self._offers = response;
      } else {
        self._offers = { execute: { mboxes: [] } };
      }
      return self;
    } catch (error) {
      logger.warn(`error initializing target ${error?.message}`);
      return self;
    }
  }

    /**
     * Check if a given offer is enabled
     * @param {string} mboxId - The mboxid to check
     * @param {object} options
     *        options.strictCheck {boolean} - Use strict checking
     * @returns {true|false|undefined} -
     *  When options.strictCheck is true:
     *    - true when the user is in test challenger group.
     *    - false when the user in test control group.
     *    - undefined when the user is not considered for test.
     *  When options.strictCheck is false:
     *    - true when the user is in test challenger group.
     *    - false otherwise
     */
    isOfferEnabled = (mboxId, { strictCheck = false } = {}) => {
      if (!mboxId) {
        if (strictCheck) {
          return undefined;
        }
        return false;
      }
      if (this._alloyInitialized) {
        const propositions = this._offers.propositions.filter(p => p.scope === mboxId);
        if (propositions?.length) {
          const { items = [] } = propositions[0];
          if (items.length === 0 && strictCheck) {
            return undefined;
          }
          return items.some(i => i.data?.content?.enabled);
        }
        if (strictCheck) {
          return undefined;
        }
        return false;
      }
      const mboxes = this._offers.execute.mboxes.filter(m => m.name === mboxId);
      if (mboxes?.length) {
        const { options = [] } = mboxes[0];
        if (options.length === 0 && strictCheck) {
          return undefined;
        }
        return options.some(o => o.content?.enabled);
      }
      if (strictCheck) {
        return undefined;
      }
      return false;
    };

    getOffer = async ({ mboxId, params = {}, getPageLoadOffers = false }) => {
      if (!this?._target && !this?._alloy) {
        logger.info('Target not loaded');
        return [{}];
      }

      if (this?._alloy) {
        const decisionScopeParams = { ...this.options.defaultParameters, ...params };
        let response = [{}];
        if (getPageLoadOffers) {
          try {
            const { propositions = [] } = await this.fetchProposition(['__view__'], decisionScopeParams);
            response = propositions;
          } catch (e) {
            logger.error(`Error: TargetAPI alloy sendEvent getPageLoadOffers: ${e}`);
          }
          return response;
        }
        try {
          const { propositions = [] } = await this.fetchProposition([mboxId], decisionScopeParams);
          response = propositions;
        } catch (e) {
          logger.error(`Error: TargetAPI alloy sendEvent: ${e}`);
        }
        const resultProposition = response.filter(p => p.scope === mboxId);
        const { content } = resultProposition?.[0]?.items?.[0]?.data || {};
        return [{
          content: content && [content],
        }];
      }

      const getOffersParams = {
        request: {
          execute: {
          },
        },
      };

      if (getPageLoadOffers) {
        getOffersParams.request.execute.pageLoad = {};
      } else {
        const offerParams = { ...this.options.defaultParameters, ...params };
        const mboxes = [{
          name: mboxId,
          parameters: offerParams,
          index: 0,
        }];
        getOffersParams.consumerId = mboxId;
        getOffersParams.request.execute.mboxes = mboxes;
      }
      let response = [{}];
      try {
        const { execute = {} } = await window.adobe.target.getOffers(getOffersParams);
        response = execute;
      } catch (e) {
        logger.error(`Error: TargetAPI.getOffers: ${e}`);
      }

      if (getPageLoadOffers) return response;

      const { content } = response.mboxes?.[0]?.options?.[0] || {};
      return [{
        content: content && [content],
      }];
    };

    /**
     * Retrieves multiple offers for the given mboxes.
     *
     * @param {Object} options - The options object.
     * @param {Array<string>} options.mboxes -  An array of mbox objects, each containing a `name` property
     * @param {Object} [options.params={}] - Additional parameters for the request.
     * @returns {Promise<Array<Object>>} A promise that resolves to an array of offer objects.
     *
     * @example
     * const options = {[{ name: 'mbox1' }, { name: 'mbox2' }], params: { param1: 'value1' }};
     * const offers = await getMultipleOffers(options);
     * console.log(offers);
     */
    getMultipleOffers = async ({ mboxes, params = {} }) => {
      if (!mboxes?.length) {
        logger.info('No mboxes provided');
        return [{}];
      }
      if (!this._target && !this._alloy) {
        logger.info('Target not loaded');
        return [{}];
      }

      if (this._alloy) {
        return this._getAlloyMultipleOffers(mboxes, params);
      }

      return this._getTargetMultipleOffers(mboxes, params);
    };

    /**
     * Fetches multiple offers from the Alloy API for the given mboxes and parameters.
     *
     * @param {Array} mboxes - An array of mbox objects, each containing a `name` property.
     * @param {Object} params - Additional parameters to be merged with default parameters.
     * @returns {Promise<Array>} A promise that resolves to an array of objects, each containing the mbox name and its corresponding content.
     *
     * @throws Will log an error if the fetchProposition call fails.
     */
    _getAlloyMultipleOffers = async (mboxes, params) => {
      const decisionScopeParams = { ...this.options.defaultParameters, ...params };
      const decisionScopes = mboxes.map(m => m.name);
      let response = [];
      try {
        const { propositions = [] } = await this.fetchProposition(decisionScopes, decisionScopeParams);
        response = propositions;
        return mboxes.map(m => {
          const resultProposition = response.find(p => p.scope === m.name);
          const { content } = resultProposition?.items?.[0]?.data || {};
          return {
            mbox: m.name,
            content: content && [content],
          };
        });
      } catch (e) {
        logger.error(`Error: TargetAPI alloy sendEvent: ${e}`);
        // Return empty results for all mboxes when fetchProposition fails
        return mboxes.map(m => ({
          mbox: m.name,
          content: undefined,
        }));
      }
    };

    /**
     * Fetches multiple offers from Adobe Target (non-alloy) for the given mboxes.
     *
     * @param {Array} mboxes - An array of mbox objects, each containing a name property.
     * @param {Object} params - Additional parameters to be merged with default parameters.
     * @returns {Promise<Array>} A promise that resolves to an array of results, each containing the mbox name and content.
     *
     */
    _getTargetMultipleOffers = async (mboxes, params) => {
      const offerParams = { ...this.options.defaultParameters, ...params };
      const mboxesTrgt = mboxes.map((m, i) => ({
        name: m.name,
        parameters: offerParams,
        index: i,
      }));
      const consumerId = mboxes.map(m => m.name).join(',');

      let response = [];
      try {
        const { execute = {} } = await window.adobe.target.getOffers({
          consumerId,
          request: {
            execute: {
              mboxes: mboxesTrgt,
            },
          },
        });
        response = execute;
        return mboxes.map(m => {
          const mboxResp = response.mboxes.find(p => p.name === m.name);
          const { content } = mboxResp?.options?.[0] || {};
          return {
            mbox: m.name,
            content: content && [content],
          };
        });
      } catch (e) {
        logger.error(`Error: TargetAPI.getOffers: ${e}`);
        // Return empty results for all mboxes when getOffers fails
        return mboxes.map(m => ({
          mbox: m.name,
          content: undefined,
        }));
      }
    };
}

export default TargetAPI;
